import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  AgentApiError,
  getAgentApiErrorMessage,
  getAgentClient,
  getAgentClients,
  getAgentOrders,
  serializeAgentClient,
  serializeAgentMeta,
  serializeClientOrderRelation,
  updateAgentClient,
} from '../../modules/b2b/api';
import { AGENT_AUTH_ROUTE } from '../../modules/b2b/auth/config';
import { withAppBasePath } from '../../modules/appMode';
import {
  fetchB2BClientsAction,
  selectB2BClientAction,
  updateB2BClientAction,
} from '../actions/b2bClients';
import {
  appendClients,
  replaceClient,
  setActiveClient,
  setActiveClientId,
  setClients,
  setError,
  setIsLoading,
  setIsLoadingActiveClient,
  setIsSaving,
  setMeta,
} from '../reducers/b2bClients';
import { activeB2BClientSelector } from '../selectors/b2bClients';
import { safeApply } from './errors';

const handleUnauthorized = (error: unknown) => {
  if (error instanceof AgentApiError && error.status === 401) {
    window.location.assign(withAppBasePath(AGENT_AUTH_ROUTE));
    return true;
  }

  return false;
};

function* fetchB2BClients({
  payload,
}: ReturnType<typeof fetchB2BClientsAction>) {
  const page = payload?.page || 1;

  yield put(setIsLoading(true));
  yield put(setError(null));

  if (page === 1) {
    yield put(setClients([]));
  }

  try {
    const response: Awaited<ReturnType<typeof getAgentClients>> = yield call(
      getAgentClients,
      payload
    );

    const clients = response.clients.map((item) =>
      serializeAgentClient(item)
    );

    yield put(page > 1 ? appendClients(clients) : setClients(clients));
    yield put(setMeta(serializeAgentMeta(response.meta)));
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsLoading(false));
  }
}

function* selectB2BClient({ payload }: ReturnType<typeof selectB2BClientAction>) {
  yield put(setActiveClientId(payload.id));
  yield put(setActiveClient(null));
  yield put(setIsLoadingActiveClient(true));
  yield put(setError(null));

  try {
    const response: Awaited<ReturnType<typeof getAgentClient>> = yield call(
      getAgentClient,
      payload.id
    );
    const ordersResponse: Awaited<ReturnType<typeof getAgentOrders>> =
      yield call(getAgentOrders, {
        query: response.client.phone,
        page: 1,
        perPage: 100,
      });

    yield put(
      setActiveClient(
        serializeAgentClient(
          response.client,
          ordersResponse.orders
            .filter(({ client }) => client.id === response.client.id)
            .map(serializeClientOrderRelation)
        )
      )
    );
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsLoadingActiveClient(false));
  }
}

function* updateB2BClient({
  payload,
}: ReturnType<typeof updateB2BClientAction>) {
  yield put(setIsSaving(true));
  yield put(setError(null));

  try {
    const response: Awaited<ReturnType<typeof updateAgentClient>> = yield call(
      updateAgentClient,
      payload.id,
      payload.client
    );
    const activeClient = yield select(activeB2BClientSelector);

    yield put(
      replaceClient(
        serializeAgentClient(response.client, activeClient?.orders || [])
      )
    );
  } catch (error) {
    if (!handleUnauthorized(error)) {
      yield put(setError(getAgentApiErrorMessage(error)));
    }
  } finally {
    yield put(setIsSaving(false));
  }
}

export default function* b2bClientsSaga() {
  yield all([
    takeLatest(fetchB2BClientsAction, safeApply(fetchB2BClients)),
    takeLatest(selectB2BClientAction, safeApply(selectB2BClient)),
    takeLatest(updateB2BClientAction, safeApply(updateB2BClient)),
  ]);
}
